import {
  EnMetricDistrict,
  EnMetricMovement,
  EnMetricSide,
} from '@shared/dto';
import { METRIC_DISTRICT_LABELS, METRIC_DISTRICT_ORDER } from '@shared/constants/metric-district.constants';
import { enumToSelectOptions } from '@shared/utils/enum.utils';
import { RomTargetDraft } from './rom-target-editor.types';

/** Same degree bound as measured ROM (KH-1434 / screening metric validator). */
export const ROM_GOAL_DEGREES = {
  MIN: -180,
  MAX: 180,
  STEP: 1,
} as const;

/**
 * Display order is not the OpenAPI declaration order. A `Record` (not an
 * array) so a new `EnMetricSide` or `EnMetricMovement` fails the build.
 * A value the rank does not name sorts after every ranked option.
 */
const ROM_SIDE_RANK: Record<EnMetricSide, number> = {
  [EnMetricSide.LEFT]: 0,
  [EnMetricSide.RIGHT]: 1,
  [EnMetricSide.BOTH]: 2,
};

const ROM_MOVEMENT_RANK: Record<EnMetricMovement, number> = {
  [EnMetricMovement.FLEXION]: 0,
  [EnMetricMovement.EXTENSION]: 1,
  [EnMetricMovement.ABDUCTION]: 2,
  [EnMetricMovement.ADDUCTION]: 3,
  [EnMetricMovement.ROTATION]: 4,
};

export const ROM_SIDE_ORDER: EnMetricSide[] = (
  Object.keys(ROM_SIDE_RANK) as EnMetricSide[]
).sort((a, b) => ROM_SIDE_RANK[a] - ROM_SIDE_RANK[b]);

export const ROM_MOVEMENT_ORDER: EnMetricMovement[] = (
  Object.keys(ROM_MOVEMENT_RANK) as EnMetricMovement[]
).sort((a, b) => ROM_MOVEMENT_RANK[a] - ROM_MOVEMENT_RANK[b]);

export const ROM_SIDE_LABELS: Record<EnMetricSide, string> = {
  [EnMetricSide.LEFT]: $localize`Sinistra`,
  [EnMetricSide.RIGHT]: $localize`Destra`,
  [EnMetricSide.BOTH]: $localize`Entrambi`,
};

export const ROM_MOVEMENT_LABELS: Record<EnMetricMovement, string> = {
  [EnMetricMovement.FLEXION]: $localize`Flessione`,
  [EnMetricMovement.EXTENSION]: $localize`Estensione`,
  [EnMetricMovement.ABDUCTION]: $localize`Abduzione`,
  [EnMetricMovement.ADDUCTION]: $localize`Adduzione`,
  [EnMetricMovement.ROTATION]: $localize`Rotazione`,
};

export const EMPTY_ROM_TARGET_DRAFT: RomTargetDraft = {
  district: null,
  side: EnMetricSide.LEFT,
  movement: null,
  goalDegrees: null,
};


/** Sort index for an enum value absent from `order`. It lands after every ranked option. */
const UNRANKED_ENUM_OPTION = Number.POSITIVE_INFINITY;

export function enumSelectOptions<T extends string>(
  enumObject: Record<string, T>,
  labels: Record<T, string>,
  order: T[],
) {
  const rank = new Map(order.map((value, index) => [value, index]));
  return enumToSelectOptions(enumObject, (value) => labels[value as T]).sort(
    (a, b) =>
      (rank.get(a.value as T) ?? UNRANKED_ENUM_OPTION) -
      (rank.get(b.value as T) ?? UNRANKED_ENUM_OPTION),
  );
}

export const ROM_DISTRICT_OPTIONS = enumSelectOptions(
  EnMetricDistrict,
  METRIC_DISTRICT_LABELS,
  METRIC_DISTRICT_ORDER,
);

export const ROM_SIDE_OPTIONS = enumSelectOptions(
  EnMetricSide,
  ROM_SIDE_LABELS,
  ROM_SIDE_ORDER,
);

export const ROM_MOVEMENT_OPTIONS = enumSelectOptions(
  EnMetricMovement,
  ROM_MOVEMENT_LABELS,
  ROM_MOVEMENT_ORDER,
);
